/**
 * screens/modals/RejectApprovalModal
 * ──────────────────────────────────────────────────────────────────────────
 * Reject a request, with the reason the requester will read.
 *
 * This modal exists BECAUSE there is no one-click reject anywhere in this
 * product. Invariant 5: a rejection carries a reason, and the person whose
 * spend was refused sees it in their own ledger. A reject button that skipped
 * this step would leave someone with a refusal and no explanation.
 *
 * The hint says the requester will see it, before the box is typed in — so
 * nobody discovers that afterwards.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useState } from 'react';
import { Modal, Textarea } from '../../components';
import { decide } from '../../services/approvals/approvals.service';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

export interface RejectApprovalModalProps {
  approvalId: string;
  /** What is being rejected, so the modal names it. */
  label: string | null;
}

export function RejectApprovalModal({ approvalId, label }: RejectApprovalModalProps) {
  const { closeModal } = useShell();
  const [reason, setReason] = useState('');

  const { submitting, run } = useModalSubmit({
    validate: () =>
      reason.trim() ? null : 'A rejection must say why. The requester sees this reason.',
    submit: () => decide(approvalId, 'reject', reason.trim()),
    title: 'Rejected',
    fallback: 'The requester can see your reason.',
    tone: 'plain',
  });

  return (
    <Modal
      visible
      title="Reject this request"
      hint={label ? `${label} will be refused.` : 'The request will be refused.'}
      onClose={closeModal}
      primaryLabel="Reject"
      onPrimary={run}
      primaryLoading={submitting}
    >
      <Textarea
        label="Reason"
        required
        hint="The requester reads this, so write it for them."
        value={reason}
        onChangeText={setReason}
        placeholder="No receipt attached — please re-submit with the invoice."
        maxLength={1000}
        rows={4}
      />
    </Modal>
  );
}

export default RejectApprovalModal;
